import React, { useState, useEffect } from 'react';
import ForecastDropdown from "./ForecastDropdown";
import ForecastPeriodDropdown from "./ForecastPeriodDropdown";
import Box from "@mui/material/Box";
import axiosInstance from "../AxiosInstance";
import { Skeleton, Paper, Grid } from "@mui/material";
import OutputsPromotionsTable from "./OutputsPromotionsTable";
import { Forecast, SummaryData } from "../types/promotions";

const ForecastOutputsPromotions = () => {
    const [forecasts, setForecasts] = useState<Forecast[]>([]);
    const [selectedForecastId, setSelectedForecastId] = useState<number | null>(null);
    const [selectedForecastPeriodId, setSelectedForecastPeriodId] = useState<number | null>(null);
    const [summaryData, setSummaryData] = useState<SummaryData>({
        groupedPromotions: [],
        weekToMonthMap: {},
    });
    const [loadingForecasts, setLoadingForecasts] = useState<boolean>(true);
    const [loadingTable, setLoadingTable] = useState<boolean>(false);

    const getToken = (): string | null => localStorage.getItem("token");

    useEffect(() => {
        const storedForecast = JSON.parse(localStorage.getItem('selectedForecast') || 'null');
        const storedForecastPeriodId = localStorage.getItem('selectedForecastPeriodId');

        if (storedForecast?.forecastId) {
            setSelectedForecastId(storedForecast.forecastId);
        }

        if (storedForecastPeriodId && storedForecastPeriodId !== "null") {
            setSelectedForecastPeriodId(parseInt(storedForecastPeriodId, 10));
        } else {
            setSelectedForecastPeriodId(null);
        }

        fetchForecastsAndPeriods();
    }, []);

    const fetchForecastsAndPeriods = async (): Promise<void> => {
        try {
            setLoadingForecasts(true);
            const token = getToken();
            const response = await axiosInstance.get("/forecasts", {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setForecasts(response.data);
        } catch (error) {
            console.log(error);
        } finally {
            setLoadingForecasts(false);
        }
    };

    useEffect(() => {
        if (!selectedForecastId || !selectedForecastPeriodId) return;

        const fetchOutputsPromotions = async (): Promise<void> => {
            setLoadingTable(true);
            try {
                const params = { forecastPeriodId: selectedForecastPeriodId };
                const response = await axiosInstance.get(
                    `/forecasts/${selectedForecastId}/outputs-promotions`,
                    { params }
                );
                setSummaryData(response.data);
            } catch (error) {
                console.error("Failed to fetch output promotions:", error);
                setSummaryData({
                    groupedPromotions: [],
                    weekToMonthMap: {},
                });
            } finally {
                setLoadingTable(false);
            }
        };

        fetchOutputsPromotions();
    }, [selectedForecastId, selectedForecastPeriodId]);

    const selectedForecast = forecasts.find((f) => f.forecastId === selectedForecastId);

    const setSelectedForecastPeriod = (forecastPeriodId: number | null) => {
        if (forecastPeriodId !== null && forecastPeriodId !== undefined) {
            localStorage.setItem('selectedForecastPeriodId', String(forecastPeriodId));
            setSelectedForecastPeriodId(forecastPeriodId);
        } else {
            localStorage.removeItem('selectedForecastPeriodId');
            setSelectedForecastPeriodId(null);
        }
    };

    const handleForecastSelected = (forecastId: number | null) => {
        setSelectedForecastId(forecastId);
        localStorage.setItem('selectedForecast', JSON.stringify({ forecastId }));
        const forecast = forecasts.find(f => f.forecastId === forecastId);
        const sortedPeriods = forecast?.forecastPeriods
            ? [...forecast.forecastPeriods].sort((a, b) => {
                const aIdx = a.periodIndex ?? a.forecastPeriodId;
                const bIdx = b.periodIndex ?? b.forecastPeriodId;
                return aIdx - bIdx;
            })
            : [];
        const firstPeriodId = sortedPeriods.length > 0 ? sortedPeriods[0].forecastPeriodId : null;
        setSelectedForecastPeriod(firstPeriodId);
    };

    // Ensure that when a forecast year is selected but the period is unset or invalid,
    // we default to the first available period (ordered by periodIndex asc)
    useEffect(() => {
        if (!selectedForecastId || forecasts.length === 0) return;
        const forecast = forecasts.find(f => f.forecastId === selectedForecastId);
        const periods = forecast?.forecastPeriods || [];
        if (periods.length === 0) return;

        const periodExists = periods.some(fp => fp.forecastPeriodId === selectedForecastPeriodId);
        if (!periodExists) {
            const first = [...periods].sort((a, b) => {
                const aIdx = a.periodIndex ?? a.forecastPeriodId;
                const bIdx = b.periodIndex ?? b.forecastPeriodId;
                return aIdx - bIdx;
            })[0];
            setSelectedForecastPeriod(first ? first.forecastPeriodId : null);
        }
    }, [forecasts, selectedForecastId, selectedForecastPeriodId]);

    return (
        <Box sx={{ height: "100vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <Paper elevation={2} sx={{ p: 2, mb: 1, flexShrink: 0 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm="auto">
                        {loadingForecasts ? (
                            <Skeleton variant="rectangular" width={200} height={56} />
                        ) : (
                            <ForecastDropdown
                                forecasts={forecasts}
                                selectedForecastId={selectedForecastId}
                                onForecastSelected={handleForecastSelected}
                                width={200}
                            />
                        )}
                    </Grid>
                    <Grid item xs={12} sm="auto">
                        {loadingForecasts ? (
                            <Skeleton variant="rectangular" width={200} height={56} />
                        ) : (
                            <ForecastPeriodDropdown
                                forecastPeriods={selectedForecast?.forecastPeriods || []}
                                selectedForecastPeriodId={selectedForecastPeriodId}
                                onForecastPeriodSelected={setSelectedForecastPeriod}
                                width={200}
                            />
                        )}
                    </Grid>
                </Grid>
            </Paper>
            <Box sx={{ flex: 1, overflow: "hidden" }}>
                {loadingTable ? (
                    <Skeleton variant="rectangular" width="100%" height="100%" />
                ) : (
                    selectedForecastId &&
                    selectedForecastPeriodId !== null && (
                        <OutputsPromotionsTable summaryData={summaryData} viewByMonth={false} />
                    )
                )}
            </Box>
        </Box>
    );
};

export default ForecastOutputsPromotions;
